import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../chat/api";
import CopilotSidePanel from "./CopilotSidePanel";

/* The requirements behind one scoring area.
 *
 * The card says "Technical Architecture & Integration — 6.8 of 19.1 points" and
 * that is the end of the story: you can see the score is bad and not one reason
 * why. This is the reason why. Every requirement the scorer matched to that
 * area, what evidence it found, and how good that evidence was.
 *
 * Fetched fresh rather than read off the card. The card's persisted payload is
 * COMPACTED — per-requirement detail is exactly what it drops to stay a
 * reasonable size in a chat row — so a card reopened from history has aggregates
 * and nothing else. qualify-status returns the full analysis for both.
 *
 * Sorted worst-first, always. Nobody opens this to admire the requirements that
 * scored 5; they open it to find what is costing them points.
 */

const BANDS = [
  // ONE colour per band, used for the text, the row marker and the filter chip.
  // There used to be a second, brighter value for the markers; it earned its
  // keep on a filled bar and nowhere else — as a dot it measured 2.80:1 and
  // 2.92:1 for adequate and strong, under the 3:1 a meaningful non-text mark
  // needs. Collapsing the two both removes a field and fixes that.
  //
  // Spelled out rather than derived from `text` at runtime: Tailwind emits only
  // the class names it can SEE in source, so text.replace("text-","bg-") yields
  // a class that never reaches the stylesheet and a dot with no colour at all.
  { key: "none",     label: "No evidence", text: "text-[#B91C1C]", chip: "bg-[#B91C1C]", hit: (s) => !s || s <= 0 },
  { key: "weak",     label: "Weak",        text: "text-[#92620B]", chip: "bg-[#92620B]", hit: (s) => s >= 1 && s <= 2 },
  { key: "adequate", label: "Adequate",    text: "text-[#5C7326]", chip: "bg-[#5C7326]", hit: (s) => s === 3 },
  { key: "strong",   label: "Strong",      text: "text-[#1F7D38]", chip: "bg-[#1F7D38]", hit: (s) => s >= 4 },
];

const bandOf = (score) => BANDS.find((b) => b.hit(Number(score) || 0)) || BANDS[0];

/* One requirement.
 *
 * The first version put eight things on every row at the same weight — dot,
 * text, band, score, separator, filename, "Show evidence", index — and repeated
 * all eight identically down the list. That is what makes a list look machine-
 * made: no editorial judgement about which of those a person actually needs.
 *
 * Now the requirement text leads, one short line of state sits under it, and
 * the whole row is the disclosure. The source document moved to the header,
 * because in practice every requirement in an area comes from the same file and
 * printing it seven times says nothing seven times.
 */
const Requirement = ({ req, area, showSource }) => {
  const [open, setOpen] = useState(false);
  const band = bandOf(req.confidence_score);
  const evidence = String(req.source_text || "").trim();
  const why = String(req.justification || "").trim();
  const hasDetail = !!(evidence || why);

  return (
    <li>
      <button
        type="button"
        onClick={() => hasDetail && setOpen((v) => !v)}
        aria-expanded={hasDetail ? open : undefined}
        disabled={!hasDetail}
        className={`flex w-full items-start gap-2.5 px-2 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none ${
          hasDetail ? "hover:bg-[#18101C]/[0.025]" : "cursor-default"
        }`}
      >
        <span className={`mt-[7px] h-[7px] w-[7px] flex-none rounded-full ${band.chip}`} aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] leading-[1.45] text-[#18101C]">{req.requirement}</span>
          <span className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[11.5px]">
            <span className={`font-semibold ${band.text}`}>{band.label}</span>
            <span className="text-[#77707E]">{Number(req.confidence_score) || 0}/5</span>
            {area && (
              <>
                <span className="text-[#77707E]" aria-hidden="true">·</span>
                <span className="min-w-0 truncate text-[#77707E]">{area}</span>
              </>
            )}
            {showSource && req.doc_name && (
              <>
                <span className="text-[#77707E]" aria-hidden="true">·</span>
                <span className="min-w-0 max-w-[180px] truncate text-[#77707E]" title={req.doc_name}>
                  {req.doc_name}
                </span>
              </>
            )}
          </span>
        </span>
        {hasDetail && (
          <svg
            aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            className={`mt-[7px] flex-none text-[#B4AEBB] transition-transform duration-200 motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </button>

      {open && hasDetail && (
        <div className="mx-2 mb-3 space-y-2 rounded-xl bg-[#18101C]/[0.03] p-3 animate-copilot-rise motion-reduce:animate-none">
          {why && <p className="text-[12px] leading-[1.6] text-[#3F3F46]">{why}</p>}
          {evidence && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#77707E]">
                Matched from your knowledge base
              </p>
              {/* A quote, marked as one. This text is not the copilot's words
                  and must never be mistaken for them. */}
              <blockquote className="border-l-2 border-[#A04A97]/30 pl-2.5 text-[12px] leading-[1.6] text-[#6B6472]">
                {evidence}
              </blockquote>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

/* `aspect` names one scoring area; leave it null for every requirement in the
 * project. `initialBand` preselects an evidence filter — that is what makes the
 * card's "58 weak" clickable, because the question behind that number is "which
 * 58", and it spans areas rather than sitting inside one. */
const CopilotRequirementsPanel = ({
  projectId,
  projectLabel,
  aspect,
  initialBand,
  visible,
  onClose,
  onBack,
}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialBand || "all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/rfp/folders/${projectId}/qualify-status/`);
      setData(res.data?.analysis || null);
      setError(res.data?.analysis ? null : "That analysis isn't available any more — run /qualify again.");
    } catch (err) {
      setError(err?.response?.data?.msg || "Couldn't load these requirements.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const model = useMemo(() => {
    const areas = data?.["capability and fit check"] || [];
    // Without an aspect this is the whole project, and each requirement carries
    // the area it was scored under so the rows stay attributable.
    const area = aspect ? areas.find((a) => a.name === aspect) : { name: null };
    if (!area) return null;
    const source = aspect
      ? area.requirements || []
      : areas.flatMap((a) => (a.requirements || []).map((r) => ({ ...r, _area: a.name })));
    const reqs = source.slice().sort(
      // Worst first. The default order is whatever the scorer emitted, which
      // buries the zero-evidence requirements among the fives.
      (a, b) => (Number(a.confidence_score) || 0) - (Number(b.confidence_score) || 0)
    );
    const counts = BANDS.reduce((acc, b) => {
      acc[b.key] = reqs.filter((r) => bandOf(r.confidence_score).key === b.key).length;
      return acc;
    }, {});
    /* One source or several? Printing the filename on all 46 rows when 46 of
       them came from the same document is repetition, not information — and
       these names are often generated blobs, so it is long repetition. Shown
       once in the header when they agree, per-row only when they differ. */
    const sources = [...new Set(reqs.map((r) => r.doc_name).filter(Boolean))];
    return { area, reqs, counts, sources };
  }, [data, aspect]);

  const shown = useMemo(() => {
    if (!model) return [];
    if (filter === "all") return model.reqs;
    return model.reqs.filter((r) => bandOf(r.confidence_score).key === filter);
  }, [model, filter]);

  const total = model?.reqs.length || 0;

  return (
    <CopilotSidePanel visible={visible} label={`${aspect} — requirements`} onClose={onClose}>
      <header className="flex-none border-b border-[#18101C]/[0.07] px-4 pb-3.5 pt-4">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            {/* The way back is a labelled control, not a bare chevron: this panel
                is two levels deep and "back to what" is a fair question. */}
            <button
              type="button"
              onClick={onBack || onClose}
              className="-ml-1 mb-1 inline-flex items-center gap-1 rounded px-1 text-[11.5px] font-medium text-[#6B6472] transition-colors hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
            >
              <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              {projectLabel || "Back"}
            </button>
            <h2 className="text-[15px] font-semibold leading-snug tracking-[-0.01em] text-[#18101C]">
              {aspect || "All requirements"}
            </h2>
            <p className="mt-1 text-[11.5px] text-[#77707E]">
              {loading
                ? "Loading…"
                : `${total} requirement${total === 1 ? "" : "s"}${aspect ? " scored here" : " across every area"}`}
            </p>
            {model?.sources.length === 1 && (
              <p
                className="mt-0.5 truncate text-[11.5px] text-[#77707E]"
                title={model.sources[0]}
              >
                from {model.sources[0]}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-[#6B6472] transition-colors hover:bg-[#18101C]/[0.06] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
          >
            <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filters carry their counts, so the distribution is readable without
            selecting anything — "58 weak" is the finding, not the filter. */}
        {model && total > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {[{ key: "all", label: "All", n: total, ink: null }, ...BANDS.map((b) => ({ key: b.key, label: b.label, n: model.counts[b.key], ink: b.chip }))]
              .filter((f) => f.n > 0)
              .map((f) => {
                const on = filter === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    aria-pressed={on}
                    /* Selected is BRAND, not near-black. A black pill among grey
                       ones is a generic control that happens to live here; the
                       selected state is the one place this row should look like
                       the rest of Sensa. Hover picks up the same hue at a tenth
                       of the strength, so the row reads as one family. */
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-[3px] text-[11.5px] font-medium transition-colors motion-reduce:transition-none ${
                      on
                        ? "bg-[#A04A97] text-white"
                        : "bg-[#18101C]/[0.05] text-[#6B6472] hover:bg-[#A04A97]/10 hover:text-[#8E4187]"
                    }`}
                  >
                    {/* The band's own colour, so the filter row speaks the same
                        language as the rows it filters. Drawn from the band's
                        TEXT colour rather than its bar colour: the bar greens
                        measure 2.45:1 and 2.55:1 here and all but disappear.
                        Goes white when selected — a coloured dot on the brand
                        fill fights it. The class is read straight off the band,
                        never assembled from another string; see BANDS. */}
                    {f.ink && (
                      <span
                        aria-hidden="true"
                        className={`h-[5px] w-[5px] flex-none rounded-full ${on ? "bg-white/80" : f.ink}`}
                      />
                    )}
                    {f.label} <span className="tabular-nums opacity-70">{f.n}</span>
                  </button>
                );
              })}
          </div>
        )}
      </header>

      <div className="sensa-scroll min-h-0 flex-1 overflow-y-auto px-2 py-1">
        {loading ? (
          <ul className="space-y-2 px-2 py-2" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="h-14 animate-pulse rounded-lg bg-[#18101C]/[0.05]" />
            ))}
          </ul>
        ) : error ? (
          <p role="alert" className="px-3 py-8 text-center text-[13px] leading-relaxed text-[#B91C1C]">
            {error}
          </p>
        ) : !model ? (
          <p className="px-3 py-8 text-center text-[13px] leading-relaxed text-[#6B6472]">
            This area isn't in the latest analysis.
          </p>
        ) : (
          <ul className="divide-y divide-[#18101C]/[0.05]">
            {shown.map((r, i) => (
              <Requirement
                key={`${r.requirement?.slice(0, 40)}-${i}`}
                req={r}
                area={r._area}
                showSource={model.sources.length > 1}
              />
            ))}
          </ul>
        )}
      </div>
    </CopilotSidePanel>
  );
};

export default CopilotRequirementsPanel;
